import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ChevronDown } from "lucide-react";

import {
  hasConsentDecision,
  acceptAll,
  rejectAll,
  savePreferences,
  getPreferences,
  type CookiePreferences,
} from "@/lib/cookieConsent";
import { isCapacitorNative } from "@/lib/capacitorPlatform";


const categories = [
  {
    key: "necessary" as const,
    label: "Strictly Necessary",
    description: "Essential for the website to function. Cannot be disabled.",
    locked: true,
  },
  {
    key: "preferences" as const,
    label: "Preferences",
    description: "Remember your settings like language and currency.",
    locked: false,
  },
  {
    key: "analytics" as const,
    label: "Analytics",
    description: "Help us understand how visitors use the website to improve it.",
    locked: false,
  },
  {
    key: "marketing" as const,
    label: "Marketing",
    description: "Used for relevant advertising and campaign measurement.",
    locked: false,
  },
];

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(getPreferences());
  const isNative = isCapacitorNative();

  useEffect(() => {
    // Show banner if no consent decision has been made
    if (!hasConsentDecision()) {
      // Small delay so the page renders first
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Also listen for external "open cookie settings" events
  useEffect(() => {
    const handler = () => {
      setPrefs(getPreferences());
      setShowCustomise(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };

  const handleSaveCustom = () => {
    savePreferences(prefs);
    setVisible(false);
  };

  const toggleCategory = (key: "preferences" | "analytics" | "marketing") => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  return (
    <AnimatePresence>
      {visible && isNative && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9998] bg-black/60"
        />
      )}
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 inset-x-0 z-[9999] p-4 md:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]"
          style={{ pointerEvents: "auto" }}
        >
          <div
            className="max-w-2xl mx-auto rounded-2xl border p-6 md:p-8 shadow-2xl backdrop-blur-xl"
            style={{
              background: "hsl(var(--bg-app) / 0.97)",
              borderColor: "hsl(var(--gold) / 0.2)",
              boxShadow: "0 -8px 40px rgba(0,0,0,0.5)",
            }}
          >
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "hsl(var(--gold))" }} />
              <div>
                <h2
                  className="text-sm font-semibold tracking-tight mb-1"
                  style={{ color: "hsl(var(--ivory))" }}
                >
                  We value your privacy
                </h2>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "hsl(var(--ivory) / 0.5)" }}
                >
                  We use cookies to keep the site working, remember your preferences, and understand how you use Exora Club.
                  You can accept all, reject non-essential, or customise your choices. Read our{" "}
                  <a
                    href="/cookie-policy"
                    className="hover:underline"
                    style={{ color: "hsl(var(--gold))" }}
                  >
                    Cookie Policy
                  </a>{" "}
                  for details.
                </p>
              </div>
            </div>

            {/* Customise panel */}
            <AnimatePresence>
              {showCustomise && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-3 mb-5 pt-3" style={{ borderTop: "1px solid hsl(var(--border))" }}>
                    {categories.map((cat) => (
                      <div
                        key={cat.key}
                        className="flex items-center justify-between py-2"
                      >
                        <div className="pr-4">
                          <p
                            className="text-xs font-medium"
                            style={{ color: "hsl(var(--ivory) / 0.8)" }}
                          >
                            {cat.label}
                          </p>
                          <p
                            className="text-[11px] mt-0.5"
                            style={{ color: "hsl(var(--ivory) / 0.35)" }}
                          >
                            {cat.description}
                          </p>
                        </div>
                        <div style={{ flex: "0 0 44px", width: "44px", height: "24px", alignSelf: "center" }}>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={prefs[cat.key]}
                            aria-label={`Toggle ${cat.label}`}
                            disabled={cat.locked}
                            onClick={() =>
                              !cat.locked && toggleCategory(cat.key as "preferences" | "analytics" | "marketing")
                            }
                            style={{
                              position: "relative",
                              display: "block",
                              boxSizing: "border-box",
                              width: "44px",
                              minWidth: "44px",
                              maxWidth: "44px",
                              height: "24px",
                              minHeight: "24px",
                              maxHeight: "24px",
                              borderRadius: "9999px",
                              padding: "2px",
                              margin: 0,
                              border: "none",
                              outline: "none",
                              background: prefs[cat.key]
                                ? "hsl(var(--gold) / 0.5)"
                                : "hsl(var(--ivory) / 0.12)",
                              transition: "background 0.2s",
                              opacity: cat.locked ? 0.5 : 1,
                              cursor: cat.locked ? "not-allowed" : "pointer",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                top: "2px",
                                left: prefs[cat.key] ? "22px" : "2px",
                                display: "block",
                                boxSizing: "border-box",
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "hsl(var(--ivory))",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                transition: "left 0.2s",
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={handleRejectAll}
                className="flex-1 py-2.5 rounded-lg text-xs tracking-[0.12em] uppercase font-medium transition-colors"
                style={{
                  border: "1px solid hsl(var(--ivory) / 0.12)",
                  color: "hsl(var(--ivory) / 0.5)",
                  background: "transparent",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "hsl(var(--ivory) / 0.25)";
                  (e.currentTarget as HTMLElement).style.color = "hsl(var(--ivory) / 0.7)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "hsl(var(--ivory) / 0.12)";
                  (e.currentTarget as HTMLElement).style.color = "hsl(var(--ivory) / 0.5)";
                }}
              >
                Reject All
              </button>
              <button
                onClick={() => {
                  if (showCustomise) {
                    handleSaveCustom();
                  } else {
                    setShowCustomise(true);
                  }
                }}
                className="flex-1 py-2.5 rounded-lg text-xs tracking-[0.12em] uppercase font-medium transition-colors flex items-center justify-center gap-1.5"
                style={{
                  border: "1px solid hsl(var(--gold) / 0.25)",
                  color: "hsl(var(--gold))",
                  background: "hsl(var(--gold) / 0.06)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "hsl(var(--gold) / 0.12)";
                  (e.currentTarget as HTMLElement).style.borderColor = "hsl(var(--gold) / 0.4)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "hsl(var(--gold) / 0.06)";
                  (e.currentTarget as HTMLElement).style.borderColor = "hsl(var(--gold) / 0.25)";
                }}
              >
                {showCustomise ? (
                  "Save Preferences"
                ) : (
                  <>
                    Customise
                    <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 py-2.5 rounded-lg text-xs tracking-[0.12em] uppercase font-medium transition-colors"
                style={{
                  background: "hsl(var(--gold) / 0.2)",
                  border: "1px solid hsl(var(--gold) / 0.4)",
                  color: "hsl(var(--gold))",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "hsl(var(--gold) / 0.3)";
                  (e.currentTarget as HTMLElement).style.borderColor = "hsl(var(--gold) / 0.6)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "hsl(var(--gold) / 0.2)";
                  (e.currentTarget as HTMLElement).style.borderColor = "hsl(var(--gold) / 0.4)";
                }}
              >
                Accept All
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
